import { ScreenReaderOnly } from '@capsule/consumer-design-system';

import { useToggle } from '@react-hookz/web';
import styled from 'styled-components';

import CapsuleIcon from 'src/app/components/Elements/CapsuleIcon';
import TextInput from 'src/app/components/Form/TextInput';
import type { TextInputUnwiredProps } from 'src/app/components/Form/TextInput';
import { CAPSULE_ICON_STRINGS } from 'src/consts';

const TogglePasswordVisibilityButton = styled.button.attrs({
  type: 'button',
  role: 'switch',
})`
  appearance: none;
  border: none;
  background-color: ${({ theme }) => theme.colors.transparent};
  padding: ${({ theme }) => theme.space.s1};

  &:hover {
    cursor: pointer;
  }

  & svg,
  & svg > path {
    color: ${({ theme }) => theme.colors.capsuleBlue50};
    fill: ${({ theme }) => theme.colors.capsuleBlue50};
  }
`;

interface PasswordInputProps
  extends Omit<TextInputUnwiredProps, 'adornment' | 'autoComplete' | 'type' | 'required' | 'aria-required'> {
  /**
   * For accessibility reasons, autocomplete should always be defined. For password inputs, it's either a new password
   * (for registration and reset forms) or a current password (for login forms).
   * @see https://incl.ca/show-hide-password-accessibility-and-password-hints-tutorial/#autocomplete
   */
  autoComplete: 'current-password' | 'new-password';
}

const visibilityButtonElementId = 'vbtneid';

export const PasswordInput = ({ autoComplete, name = 'password', ...props }: PasswordInputProps) => {
  const [isValueVisible, toggleValueVisibility] = useToggle(false);

  const togglePasswordVisibility = () => toggleValueVisibility();

  return (
    <>
      {/**
       * VoiceOver nor NVDA announces state changes to aria-pressed, so we help those users out with this.
       * @see https://a11ysupport.io/tech/aria/aria-pressed_attribute#support-table-4
       */}
      <ScreenReaderOnly aria-live="polite">
        {name} value is currently {isValueVisible ? 'visible' : 'hidden'}.
      </ScreenReaderOnly>

      <TextInput
        adornment={{
          location: 'right',
          component: (
            <TogglePasswordVisibilityButton
              onClick={togglePasswordVisibility}
              role="switch"
              aria-pressed={isValueVisible}
              id={visibilityButtonElementId}
            >
              <>
                {/* Don't swap text content because announcement would confuse a user that engaged the toggle */}
                <ScreenReaderOnly>Toggle password visibility</ScreenReaderOnly>
                <CapsuleIcon
                  iconString={isValueVisible ? CAPSULE_ICON_STRINGS.invisibility : CAPSULE_ICON_STRINGS.visibility}
                />
              </>
            </TogglePasswordVisibilityButton>
          ),
        }}
        aria-describbedby={visibilityButtonElementId}
        aria-required // required attribute has many a11y issues, so we're using aria-required instead.
        autoComplete={autoComplete}
        name={name}
        type={isValueVisible ? 'text' : 'password'}
        {...props}
      />
    </>
  );
};
